import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, ExternalLink, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Display name shown in the body copy. */
  recipientName: string;
  /** Email the invitation was sent to (informational). */
  recipientEmail: string;
  /** The Clerk invitation URL — shareable when email doesn't land. */
  inviteUrl: string;
  /** "new" for first-time invites, "reset" for password resets. */
  variant?: "new" | "reset";
}

/**
 * Surfaces the Clerk invitation URL alongside the "we sent an email"
 * confirmation — a copyable link the admin can share directly (Slack /
 * WhatsApp / SMS) if the recipient doesn't find the email.
 */
export function InviteLinkDialog({
  open,
  onClose,
  recipientName,
  recipientEmail,
  inviteUrl,
  variant = "new",
}: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invitation link copied to clipboard");
      // Brief flash, then revert in case the dialog stays open and they
      // want to copy a second time.
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select the link manually and copy.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* `overflow-hidden` is the belt to the `max-w-lg` braces — without
          it, a long invitation URL inside a `truncate` flex layout could
          push the dialog wider than its max-width on some browsers. */}
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            {variant === "new" ? "Invitation sent" : "Reset link sent"}
          </DialogTitle>
          <DialogDescription>
            {variant === "new"
              ? `An email is on its way to ${recipientEmail}. ${recipientName} will follow the link to set a password and finish signing up.`
              : `An email is on its way to ${recipientEmail}. ${recipientName} will follow the link to set a new password.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Invitation link (optional — share directly if needed)
            </div>
            {/* URL on its own row, wrapping with `break-all`. Buttons go
                on a separate row so the long token can't push them off
                the dialog. */}
            <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs break-all leading-relaxed max-h-32 overflow-auto">
              {inviteUrl}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copy}>
                {copied ? (
                  <Check className="mr-1.5 h-4 w-4" />
                ) : (
                  <Copy className="mr-1.5 h-4 w-4" />
                )}
                {copied ? "Copied" : "Copy link"}
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href={inviteUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-1.5 h-4 w-4" />
                  Open
                </a>
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Anyone with this link can sign up as {recipientName}. Share it the way you'd share
              a password.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
